/**
 * Profile Lightbox - Portable public profile viewer
 * Used on: live page, host page (via chat widget and viewer lists)
 *
 * Usage: openProfileLightbox(customerId, preloadData)
 *   preloadData (optional): { screenName, profileImage, badge, bidderNumber }
 *   Provides instant display while full profile fetches from API.
 */

(function() {
  'use strict';

  var cache = {}; // customerId -> { data, expiry }
  var CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  var SERVER_URL = (window.MHV && MHV.SERVER_URL) || '';

  // Platform metadata for link rendering
  var LINK_PLATFORMS = {
    whatnot:          { label: 'Whatnot',         domain: 'whatnot.com',         prefix: '@', urlFn: function(v) { return 'https://www.whatnot.com/user/' + v; } },
    instagram:        { label: 'Instagram',       domain: 'instagram.com',       prefix: '@', urlFn: function(v) { return 'https://www.instagram.com/' + v; } },
    facebook:         { label: 'Facebook',        domain: 'facebook.com',        prefix: null },
    ebay:             { label: 'eBay',             domain: 'ebay.com',            prefix: null },
    etsy:             { label: 'Etsy',             domain: 'etsy.com',            prefix: null },
    liveauctioneers:  { label: 'LiveAuctioneers',  domain: 'liveauctioneers.com', prefix: null },
    auctionzip:       { label: 'AuctionZip',       domain: 'auctionzip.com',      prefix: null },
    website:          { label: 'Website',          domain: null,                  prefix: null },
  };

  // --- Helpers ---

  // Quote-safe: this escaper is used in quoted attribute contexts (src/href/alt of
  // ANOTHER customer's profile), so it must also escape " and ' (SEC-61). Escaping
  // quotes is harmless in text contexts — the browser decodes the entities on render.
  function esc(str) {
    var d = document.createElement('div');
    d.appendChild(document.createTextNode(str || ''));
    return d.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Only allow safe link schemes; block javascript:/data:/etc.
  // Bare domains get https:// prepended; anything unsafe falls back to '#'.
  function safeHref(url) {
    var v = (url || '').trim();
    if (!v) return '#';
    if (/^(https?:\/\/|mailto:)/i.test(v)) return v;
    if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return '#'; // some other scheme (javascript:, data:, etc.)
    return 'https://' + v; // looks like a bare domain/path
  }

  function getInitial(name) {
    return (name || '?').charAt(0).toUpperCase();
  }

  var GLOBE_SVG = '<svg class="profile-lb-link-icon" viewBox="0 0 24 24" fill="none"><rect width="24" height="24" rx="6" fill="#4A90D9"/><circle cx="12" cy="12" r="5.5" stroke="#fff" stroke-width="1.5" fill="none"/><ellipse cx="12" cy="12" rx="2.5" ry="5.5" stroke="#fff" stroke-width="1.2" fill="none"/><line x1="6.5" y1="12" x2="17.5" y2="12" stroke="#fff" stroke-width="1.2"/></svg>';

  function faviconUrl(domain) {
    if (!domain) return '';
    return 'https://www.google.com/s2/favicons?domain=' + domain + '&sz=64';
  }

  // --- Ensure lightbox container exists ---

  function ensureContainer() {
    if (document.getElementById('profile-lightbox')) return;
    var el = document.createElement('div');
    el.id = 'profile-lightbox';
    el.onclick = function(e) { if (e.target === el) closeProfileLightbox(); };
    el.innerHTML = '<div class="profile-lb-content" id="profile-lb-content"></div>';
    document.body.appendChild(el);
  }

  // --- Render ---

  function renderProfile(profile, loading) {
    var content = document.getElementById('profile-lb-content');
    if (!content) return;

    // Real name ("First L.") — server already blanks it when the user hides it.
    var realName = profile.firstName
      ? esc(profile.firstName) + (profile.lastInitial ? ' ' + esc(profile.lastInitial) : '')
      : '';
    // Header name: screen name → real name → "Bidder #n" → Unknown.
    var headerName;
    if (profile.screenName) headerName = esc(profile.screenName);
    else if (realName) headerName = realName;
    else if (profile.bidderNumber) headerName = 'Bidder #' + esc(profile.bidderNumber);
    else headerName = 'Unknown';

    var avatarHtml;
    if (profile.profileImage) {
      avatarHtml = '<div class="profile-lb-avatar"><img src="' + esc(profile.profileImage) + '" alt=""></div>';
    } else {
      avatarHtml = '<div class="profile-lb-avatar"><div class="profile-lb-avatar-placeholder">' + getInitial(profile.screenName || profile.firstName) + '</div></div>';
    }

    var metaHtml = '';
    if (profile.badge || profile.bidderNumber) {
      metaHtml = '<div class="profile-lb-meta">';
      if (profile.badge) metaHtml += '<span class="profile-lb-badge">' + esc(profile.badge) + '</span>';
      if (profile.bidderNumber) metaHtml += '<span class="profile-lb-bidder-pill">#' + esc(profile.bidderNumber) + '</span>';
      metaHtml += '</div>';
    }

    // Show the real name as a secondary line only when the header is the screen name
    var nameHtml = (profile.screenName && realName)
      ? '<div class="profile-lb-real-name">' + realName + '</div>'
      : '';

    var bioHtml = '';
    if (profile.bio) {
      bioHtml = '<div class="profile-lb-bio">' + esc(profile.bio) + '</div>';
    }

    var linksHtml = '';
    if (profile.links) {
      var rows = '';
      for (var key in profile.links) {
        if (!profile.links.hasOwnProperty(key)) continue;
        var val = profile.links[key];
        if (!val) continue;
        var platform = LINK_PLATFORMS[key];
        if (!platform) continue;

        var iconSrc = faviconUrl(platform.domain);
        var iconHtml = iconSrc
          ? '<img class="profile-lb-link-icon" src="' + iconSrc + '" alt="">'
          : GLOBE_SVG;

        var displayText, href;
        if (platform.prefix) {
          displayText = platform.prefix + esc(val);
          href = platform.urlFn ? platform.urlFn(val) : '#';
        } else {
          displayText = esc(val.replace(/^https?:\/\/(www\.)?/, ''));
          href = safeHref(val);
        }

        rows += '<div class="profile-lb-link-row">' +
          iconHtml +
          '<div>' +
            '<a class="profile-lb-link-text" href="' + esc(href) + '" target="_blank" rel="noopener">' + displayText + '</a>' +
            '<div class="profile-lb-link-label">' + esc(platform.label) + '</div>' +
          '</div>' +
        '</div>';
      }
      if (rows) linksHtml = '<div class="profile-lb-links">' + rows + '</div>';
    }

    var loadingHtml = loading ? '<div class="profile-lb-loading">Loading profile...</div>' : '';
    var errorHtml = (!loading && profile.loadError) ? '<div class="profile-lb-empty">Couldn\'t load profile. Please try again.</div>' : '';
    var emptyHtml = (!loading && !profile.loadError && !profile.bio && !linksHtml) ? '<div class="profile-lb-empty">This user hasn\'t set up their profile yet.</div>' : '';

    content.innerHTML =
      '<div class="profile-lb-header">' +
        avatarHtml +
        '<div class="profile-lb-screen-name">' + headerName + '</div>' +
        metaHtml +
        nameHtml +
      '</div>' +
      '<div class="profile-lb-body">' +
        loadingHtml +
        bioHtml +
        linksHtml +
        errorHtml +
        emptyHtml +
      '</div>' +
      '<div class="profile-lb-footer">' +
        '<span class="profile-lb-close" onclick="closeProfileLightbox()">\u00D7 CLOSE</span>' +
      '</div>';
  }

  // --- Open / Close ---

  // Back button support
  window.addEventListener('popstate', function(e) {
    if (e.state && e.state.profileLightbox) return; // opening state, ignore
    var el = document.getElementById('profile-lightbox');
    if (el && el.classList.contains('visible')) {
      el.classList.remove('visible');
    }
  });

  // Shared opener. `cacheKey` namespaces the client cache so an id-keyed and a
  // bidder-keyed lookup for the same person can't collide, and `url` decides which
  // endpoint answers. See openProfileLightboxByBidder below for why both exist. (Q28.)
  function openProfile(cacheKey, url, preloadData) {
    ensureContainer();

    var profile = preloadData || {};
    if (!profile.screenName) profile.screenName = '...';

    // Push history state so back button closes lightbox
    history.pushState({ profileLightbox: true }, '');

    // Show immediately with preloaded data
    renderProfile(profile, true);
    document.getElementById('profile-lightbox').classList.add('visible');

    // Check cache
    var cached = cache[cacheKey];
    if (cached && cached.expiry > Date.now()) {
      renderProfile(cached.data, false);
      return;
    }

    // Fetch full profile
    var fetchFn = window.mhvFetch || fetch;
    fetchFn(url)
      .then(function(r) {
        if (!r.ok) {
          // Non-2xx (rate limit, server error, etc.) — treat as load failure
          var err = new Error('HTTP ' + r.status);
          err.status = r.status;
          throw err;
        }
        return r.json();
      })
      .then(function(data) {
        if (data.success && data.profile) {
          // Merge preload data (avatar/badge from socket) with API data
          var full = data.profile;
          if (!full.profileImage && preloadData && preloadData.profileImage) full.profileImage = preloadData.profileImage;
          if (!full.badge && preloadData && preloadData.badge) full.badge = preloadData.badge;

          cache[cacheKey] = { data: full, expiry: Date.now() + CACHE_TTL };
          // Only update if lightbox is still open
          if (document.getElementById('profile-lightbox').classList.contains('visible')) {
            renderProfile(full, false);
          }
        } else if (data.success === false || data.error) {
          // API returned a 404 / not-found style response — profile genuinely doesn't exist
          if (document.getElementById('profile-lightbox').classList.contains('visible')) {
            renderProfile(profile, false);
          }
        } else {
          // Unexpected shape — treat as load failure
          if (document.getElementById('profile-lightbox').classList.contains('visible')) {
            profile.loadError = true;
            renderProfile(profile, false);
          }
        }
      })
      .catch(function() {
        // Network error or non-2xx — show explicit error, NOT the empty "hasn't set up" message
        if (document.getElementById('profile-lightbox').classList.contains('visible')) {
          profile.loadError = true;
          renderProfile(profile, false);
        }
      });
  }

  window.openProfileLightbox = function(customerId, preloadData) {
    if (!customerId || customerId === 'absentee') return;
    openProfile(String(customerId),
      SERVER_URL + '/api/public-profile/' + encodeURIComponent(customerId), preloadData);
  };

  // ⭐ Open a profile by BIDDER NUMBER (Q28, 2026-08-15).
  //
  // Public surfaces (the live-page viewer list) no longer receive Shopify customer
  // ids at all — the roster broadcast stopped sending them, because a customer id is
  // internal and a bidder number is not. Those surfaces open a profile with the
  // number they legitimately have.
  //
  // Admin surfaces and chat still use openProfileLightbox with the id they already
  // hold; the server returns the identical public-safe shape either way.
  window.openProfileLightboxByBidder = function(bidderNumber, preloadData) {
    bidderNumber = String(bidderNumber == null ? '' : bidderNumber).trim();
    if (!bidderNumber) return;
    openProfile('bidder:' + bidderNumber,
      SERVER_URL + '/api/public-profile/bidder/' + encodeURIComponent(bidderNumber), preloadData);
  };

  // Invalidate client cache for a specific customer (called on profile-updated socket event)
  window.invalidateProfileCache = function(customerId) {
    if (customerId) delete cache[customerId];
  };

  window.closeProfileLightbox = function() {
    var el = document.getElementById('profile-lightbox');
    if (el && el.classList.contains('visible')) {
      el.classList.remove('visible');
      // Pop the history state we pushed on open
      if (history.state && history.state.profileLightbox) {
        history.back();
      }
    }
  };
})();
